// @flow
import * as React from "react";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import Money from "../Money";
import {
  Wallet,
  Coins,
  CoinsPlus,
  Person,
  Podium,
  Cup
} from "../Icons/Tournaments";
import { tournamentCurrency } from "../Icons/Kuchka";
import * as S from "../Tournaments/Popups/styles";
import HowMuchInBucks from "../HowMuchInBucks";

const InfinityIcon = React.memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 17.502 17.502"
    width="100%"
  >
    <path
      d="M13.182 4.431a4.282 4.282 0 0 0-3.036 1.246L8.75 7.017l-1.375-1.32C6.56 4.88 5.474 4.431 4.32 4.431s-2.239.451-3.053 1.266A4.288 4.288 0 0 0 0 8.752c0 1.152.449 2.238 1.266 3.053a4.285 4.285 0 0 0 3.054 1.266 4.272 4.272 0 0 0 3.036-1.248l1.395-1.338 1.376 1.32c.815.816 1.901 1.266 3.055 1.266s2.238-.449 3.053-1.266c.817-.814 1.267-1.9 1.267-3.055s-.449-2.238-1.266-3.055a4.296 4.296 0 0 0-3.054-1.264zm-7.576 5.605c-.687.688-1.884.688-2.572 0a1.807 1.807 0 0 1-.533-1.285c0-.486.189-.941.535-1.287.342-.344.799-.533 1.284-.533s.942.189 1.305.551l1.321 1.27-1.34 1.284zm8.861 0c-.687.689-1.866.705-2.59-.018l-1.321-1.27 1.339-1.285c.688-.688 1.886-.688 2.573-.002.344.346.533.801.533 1.287s-.19.944-.534 1.288z"
      fill="currentColor"
    />
  </svg>
));

const WhiteFree = styled.div`
  font-weight: bold;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "16px"};
  color: #fff;
  display: flex;
  align-items: center;
`;

const RefillValue = styled.span`
  margin-left: ${({ marginLeft }) => marginLeft}px;
  color: ${({ isActive }) =>
    isActive ? "#ffcc00" : "rgba(255, 255, 255, 0.44)"};
  font-weight: 700;
`;

const RefillInfinity = styled.span`
  margin: 0 4px;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  color: #ffcc00;
`;

const RefillCount = styled.span`
  font-weight: 400;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  font-size: 16px;
  line-height: 1.25;

  @media (max-width: 959px) {
    :not(:first-of-type) {
      margin-top: 15px;
    }
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  flex: 0;

  @media (max-width: 959px) {
    flex-basis: 62px;
    width: 62px;
  }

  ${({ theme }) => {
    if (theme.direction === "rtl")
      return css`
        margin-left: 10px;
      `;

    return css`
      margin-right: 10px;
    `;
  }};
`;

const Title = styled.div`
  font-size: 12px;
  margin-bottom: 5px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
`;

const Strong = styled.span`
  font-weight: 700;
`;

const Placeholder = styled(Strong)`
  color: ${({ theme }) => theme.lightWhiteForeground};
`;

const Accent = styled.span`
  color: ${({ theme }) => theme.alternativeAccent};
`;

const RefillLimit = styled.span`
  font-weight: 700;
  color: #ffcc00;
`;

type SharedProps = {|
  className?: string,
  IconWrapper?: any
|};

type InfoProps = {
  ...SharedProps,
  icon: React.Node,
  title: React.Node,
  children: React.Node
};

export function InfoWireframe({
  className,
  icon,
  IconWrapper = IconContainer,
  title,
  children
}: InfoProps) {
  return (
    <Container className={className}>
      <IconWrapper>{icon}</IconWrapper>
      <Content>
        <Title>{title}:</Title>
        <span>{children}</span>
      </Content>
    </Container>
  );
}

type BalanceProps = {
  ...SharedProps,
  participation_status: 0 | 1 | 2,
  balance: ?string
};

export function Balance({
  balance,
  participation_status,
  ...props
}: BalanceProps) {
  if (balance == null) {
    return null;
  }

  return (
    <InfoWireframe
      {...props}
      icon={<Wallet width="45px" />}
      title={<Gettext>Tournament balance</Gettext>}
    >
      <Money Value={S.Value} Symbol={S.Currency} currency={tournamentCurrency}>
        {balance}
      </Money>
    </InfoWireframe>
  );
}

type CostProps = {
  ...SharedProps,
  participation_price: string,
  participation_price_usd: string,
  type: "free" | "pay"
};

export function Cost({
  participation_price,
  participation_price_usd,
  type,
  ...props
}: CostProps) {
  return (
    <InfoWireframe
      {...props}
      icon={<Coins width="62px" />}
      title={<Gettext>Participation fee</Gettext>}
    >
      {props.subtype === "demo" ? (
        <Money Value={S.Value} Symbol={S.Currency}>
          {participation_price}
        </Money>
      ) : (!props.is_limited && type === "free") ||
        (props.is_limited && props.invited) ? (
        <Placeholder>
          <Gettext>Free</Gettext>
        </Placeholder>
      ) : (
        <HowMuchInBucks
          value={participation_price}
          valueUSD={participation_price_usd}
          valueStyle={S.Value}
          symbolStyle={S.Currency}
          divider="&nbsp;/&nbsp;"
        />
      )}
    </InfoWireframe>
  );
}

type ParticipantsProps = {
  ...SharedProps,
  participant_count: number
};

export function Participants({
  participant_count,
  ...props
}: ParticipantsProps) {
  return (
    <InfoWireframe
      {...props}
      icon={<Person width="42px" />}
      title={<Gettext>Participants</Gettext>}
    >
      <Placeholder>{participant_count}</Placeholder>
    </InfoWireframe>
  );
}

type RatingProps = {
  ...ParticipantsProps,
  current_place?: number
};

export function Rating({
  current_place,
  participant_count,
  ...props
}: RatingProps) {
  if (current_place == null) {
    return null;
  }

  return (
    <InfoWireframe
      {...props}
      icon={<Podium width="54px" />}
      title={<Gettext>Your rating</Gettext>}
    >
      <Accent>
        # <Strong>{current_place}</Strong>{" "}
      </Accent>
      <Gettext>of</Gettext>
      <Placeholder> {participant_count}</Placeholder>
    </InfoWireframe>
  );
}

const RefillInfo = ({ refillLimit, refillCount, marginLeft = 7 }) => {
  if (refillLimit) {
    return (
      <RefillValue isActive={refillLimit > refillCount} marginLeft={marginLeft}>
        ({refillCount}/{refillLimit})
      </RefillValue>
    );
  }

  return (
    <React.Fragment>
      <RefillInfinity>
        <InfinityIcon />
      </RefillInfinity>
      <RefillCount>({refillCount})</RefillCount>
    </React.Fragment>
  );
};

type RefillsProps = {
  ...SharedProps,
  refill: false | number,
  cost: null | string,
  cost_usd: null | string
};

export function Refills({
  refill,
  refillLimit,
  refillAccount,
  refillCosts,
  tournamentType,
  cost,
  cost_usd,
  ...props
}: RefillsProps) {
  const refillCount = refillAccount !== null ? refillAccount.refill_count : 0;
  const no_purchases =
    refill === false ||
    cost == null ||
    cost_usd == null ||
    (refillAccount != null &&
      refillLimit !== false &&
      refillAccount.refill_count >= refillLimit);
  const limit = refillLimit ? <RefillLimit>{refillLimit} </RefillLimit> : null;
  const refillCountByType = () => {
    if (refillType() !== "mixed") {
      return refillCount;
    }

    const reducedObj = refillCosts.reduce(
      (acc, el) => {
        if (parseFloat(el.cost_usd) === 0) {
          return {
            free: acc.free + 1,
            paid: acc.paid
          };
        }
        return {
          free: acc.free,
          paid: acc.paid + 1
        };
      },
      { free: 0, paid: 0 }
    );

    if (refillCosts.length < refillLimit) {
      parseFloat(refillCosts[refillCosts.length - 1].cost_usd) === 0
        ? (reducedObj.free =
            reducedObj.free + (refillLimit - refillCosts.length))
        : (reducedObj.paid =
            reducedObj.paid + (refillLimit - refillCosts.length));
    }

    return reducedObj;
  };
  const usedRefillByType = () => {
    if (refillAccount == null || refillAccount.refill_count === 0)
      return { free: 0, paid: 0 };
    if (refillType() !== "mixed") {
      return refillCount;
    }

    const reducedObj = refillCosts.reduce(
      (acc, el, index) => {
        if (parseFloat(el.cost_usd) === 0) {
          return {
            free: refillAccount.refill_count > index ? acc.free + 1 : acc.free,
            paid: acc.paid
          };
        }
        return {
          free: acc.free,
          paid: refillAccount.refill_count > index ? acc.paid + 1 : acc.paid
        };
      },
      { free: 0, paid: 0 }
    );

    if (refillCosts.length < refillAccount.refill_count) {
      parseFloat(refillCosts[refillCosts.length - 1].cost_usd) === 0
        ? (reducedObj.free =
            reducedObj.free + (refillAccount.refill_count - refillCosts.length))
        : (reducedObj.paid =
            reducedObj.paid +
            (refillAccount.refill_count - refillCosts.length));
    }

    return reducedObj;
  };

  const refillType = () => {
    if (no_purchases || refillCosts.length < 1) {
      return "no purchases";
    }

    if (
      refillCosts.every(
        el => parseFloat(el.cost) === 0 && parseFloat(el.cost_usd) === 0
      )
    ) {
      return "all free";
    }

    if (
      refillCosts.every(
        el => parseFloat(el.cost) > 0 && parseFloat(el.cost_usd) > 0
      )
    ) {
      return "all paid";
    }

    return "mixed";
  };

  const freeRefillLimit =
    refillType() === "mixed"
      ? refillLimit
        ? refillCountByType()["free"]
        : !(parseFloat(refillCosts[refillCosts.length - 1].cost_usd) === 0)
        ? refillCosts.filter(el => parseFloat(el.cost_usd) === 0).length
        : false
      : refillLimit;

  const paidRefillLimit =
    refillType() === "mixed"
      ? refillLimit
        ? refillCountByType()["paid"]
        : !(parseFloat(refillCosts[refillCosts.length - 1].cost_usd) > 0)
        ? refillCosts.filter(el => parseFloat(el.cost_usd) > 0).length
        : false
      : refillLimit;

  const currentPaidRefill = () => {
    if (refillAccount == null || refillCount === 0) {
      return refillCosts.find(el => el.cost_usd > 0);
    }

    if (refillCount >= refillCosts.length) {
      return refillCosts[refillCosts.length - 1];
    }

    for (let i = refillCount; refillCosts.length - 1; i++) {
      if (refillCosts[i].cost_usd > 0) {
        return refillCosts[i];
      }
    }
  };

  return (
    <InfoWireframe
      {...props}
      icon={<CoinsPlus width="62px" />}
      title={
        <span>
          {limit}
          <Gettext>Refill fee</Gettext>
        </span>
      }
    >
      {no_purchases ? (
        <Placeholder>
          <Gettext>No refill available</Gettext>
        </Placeholder>
      ) : refillType() === "all free" ? (
        <WhiteFree>
          <Gettext>Free</Gettext>
          <RefillInfo refillLimit={refillLimit} refillCount={refillCount} />
        </WhiteFree>
      ) : refillType() === "all paid" ? (
        <HowMuchInBucks
          value={cost || "0.00"}
          valueUSD={cost_usd || "0.00"}
          valueStyle={S.Value}
          symbolStyle={S.Currency}
          divider="&nbsp;/&nbsp;"
        />
      ) : (
        <React.Fragment>
          <WhiteFree>
            <Gettext>Free</Gettext>
            <RefillInfo
              refillLimit={freeRefillLimit}
              refillCount={usedRefillByType()["free"]}
            />
          </WhiteFree>
          <div
            style={{
              marginLeft: -2
            }}
          >
            <HowMuchInBucks
              value={currentPaidRefill()["cost"] || "0.00"}
              valueUSD={currentPaidRefill()["cost_usd"] || "0.00"}
              valueStyle={S.Value}
              symbolStyle={S.Currency}
              divider="&nbsp;/&nbsp;"
            />
            {paidRefillLimit && (
              <RefillInfo
                refillCount={usedRefillByType()["paid"]}
                refillLimit={paidRefillLimit}
                marginLeft={5}
              />
            )}
          </div>
        </React.Fragment>
      )}
    </InfoWireframe>
  );
}

type PrizeProps = {
  prize_fund: string,
  prize_fund_usd: string
};

export function PrizeFund({
  prize_fund,
  prize_fund_usd,
  ...props
}: PrizeProps) {
  return (
    <InfoWireframe
      {...props}
      icon={<Cup width="27px" />}
      title={<Gettext>Prize fund</Gettext>}
    >
      <HowMuchInBucks
        value={prize_fund}
        valueUSD={prize_fund_usd}
        valueStyle={S.Value}
        symbolStyle={S.Currency}
      />
    </InfoWireframe>
  );
}



// WEBPACK FOOTER //
// ./src/components/Tournament/Info.js